import {
    Component,
    ComponentInterface,
    Host,
    h,
    Event,
    EventEmitter,
    Prop,
    State,
    Watch,
} from "@stencil/core";

/** @static base class name */
const baseClass = "ux-dot-pagination";

@Component({
    tag: "ux-dot-pagination",
    styleUrl: "dot-pagination.scss",
    shadow: false,
})
export class DotPagination implements ComponentInterface {
    /** Total pages */
    @Prop() total: number = 0;
    /** The current page  */
    @Prop() page: number = 1;
    /** The maximum number of visible dots  */
    @Prop() maxDots: number = 7;
    /** Flag that indicates that no arrow buttons should be rendered */
    @Prop() withoutArrows: boolean = false;
    /** Set to `true` if clicking an arrow on the last page should jump to the first page and vice versa */
    @Prop() loop: boolean = false;

    /** The current index */
    @State() current: number = this.page - 1;
    /** Storage for the page indexes */
    @State() items: number[] = [];

    @Watch("total")
    setTotal() {
        const { total } = this;
        this.items =
            total > 0
                ? Array(total)
                      .fill(1)
                      .map((_item, index) => index)
                : [];
    }

    @Watch("page")
    setPage() {
        this.setCurrent(this.page - 1);
    }

    /** Emitted when a dot or arrow was clicked `<{ page: number; }>` */
    @Event({ eventName: "uxPaginationChange", cancelable: true, bubbles: true })
    paginationChange: EventEmitter<{
        page: number;
    }>;

    private setCurrent = (index: number): void => {
        this.current = Math.max(-1, Math.min(this.total - 1, index));
    };

    private handleClickDotAt = (index) => {
        if (index === this.current) {
            return;
        }
        const event = this.paginationChange.emit({
            page: index + 1,
        });
        if (event.defaultPrevented) {
            return;
        }
        this.setCurrent(index);
    };

    private handlePrevPage = () => {
        if (!this.loop && this.current <= 0) {
            return;
        }
        const index = this.current > 0 ? this.current - 1 : this.total - 1;
        this.handleClickDotAt(index);
    };

    private handleNextPage = () => {
        if (!this.loop && this.current >= this.total - 1) {
            return;
        }
        const index = this.current < this.total - 1 ? this.current + 1 : 0;
        this.handleClickDotAt(index);
    };

    componentWillLoad() {
        this.setTotal();
    }

    render() {
        const {
            current,
            items,
            total,
            maxDots,
            withoutArrows,
            loop,
            handleClickDotAt,
            handlePrevPage,
            handleNextPage,
        } = this;
        const limit = Math.max(3, maxDots);
        const activeDot = Math.max(0, Math.min(current, total - 1));
        const startIndex =
            total > limit
                ? Math.min(
                      total - limit,
                      Math.max(0, Math.round(current - 0.5 * (limit - 1)))
                  )
                : 0;
        const endIndex = startIndex + Math.min(total, limit);
        return (
            <Host
                class={{
                    [baseClass]: true,
                }}
            >
                <div class={`${baseClass}__container`}>
                    {!withoutArrows && (
                        <ux-icon
                            name="chevron-right"
                            weight="normal"
                            class={{
                                [`${baseClass}__arrow`]: true,
                                [`${baseClass}__arrow--prev`]: true,
                                [`${baseClass}__arrow--disabled`]:
                                    !loop && current === 0,
                            }}
                            onClick={handlePrevPage}
                        />
                    )}
                    <div class={`${baseClass}__inner`}>
                        {items.map((listIndex) => (
                            <span
                                role="listitem"
                                class={{
                                    [`${baseClass}__item`]: true,
                                    [`${baseClass}__item--active`]:
                                        listIndex === activeDot,
                                    [`${baseClass}__item--hidden`]:
                                        listIndex < startIndex ||
                                        listIndex >= endIndex,
                                    [`${baseClass}__item--small`]:
                                        (listIndex === startIndex &&
                                            listIndex > 0) ||
                                        (listIndex === endIndex - 1 &&
                                            listIndex < total - 1),
                                }}
                                onClick={() => handleClickDotAt(listIndex)}
                            >
                                <span class={`${baseClass}__dot`} />
                            </span>
                        ))}
                    </div>
                    {!withoutArrows && (
                        <ux-icon
                            name="chevron-right"
                            weight="normal"
                            class={{
                                [`${baseClass}__arrow`]: true,
                                [`${baseClass}__arrow--next`]: true,
                                [`${baseClass}__arrow--disabled`]:
                                    !loop && current === total - 1,
                            }}
                            onClick={handleNextPage}
                        />
                    )}
                </div>
            </Host>
        );
    }
}
